import { createJsonClient } from '../../../shared/httpClient';
import { payrollConfigApiBaseUrl, paymentProcessingApiBaseUrl } from '../../../shared/env';
import { ApiError } from '../../identity/types';
import type {
  AddressStepResponse,
  BasicInfoStepResponse,
  BusinessProfileResponse,
  CreateFundingAccountRequest,
  CreateMembershipInvitationRequest,
  CreatePayScheduleRequest,
  FundingAccountDraftResponse,
  FundingAccountResponse,
  MembershipInvitationResponse,
  MembershipResponse,
  PayScheduleDraftResponse,
  PayScheduleResponse,
  SaveAddressRequest,
  SaveBasicInfoRequest,
  SaveFundingAccountDraftRequest,
  SavePayScheduleDraftRequest,
  SaveWorkLocationDraftRequest,
  SubmitBusinessProfileResponse,
  WorkLocationDraftResponse,
  WorkLocationRequest,
  WorkLocationResponse,
} from '../types';
import type { IBusinessProfileSetupGateway } from './IBusinessProfileSetupGateway';

// Identity.Api itself — same-origin relative path (this feature is entirely on the normal authenticated
// `epn_at` session, never the onboarding scheme AdminSignupGateway uses).
const identityRequest = createJsonClient('');
const payrollConfigRequest = createJsonClient(payrollConfigApiBaseUrl);
const paymentProcessingRequest = createJsonClient(paymentProcessingApiBaseUrl);

function notFoundAsNull<T>(promise: Promise<T>): Promise<T | null> {
  return promise.catch((err) => {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  });
}

/** Talks to the three real backend modules this feature spans (Identity.Api, PaymentProcessing.Api,
 * PayrollConfig.Api) — see BusinessProfileSetupGateway.ts for the VITE_USE_MOCKS selection. */
export const httpBusinessProfileSetupGateway: IBusinessProfileSetupGateway = {
  // ---- Overview / Final Submit ----
  getProfile: () => identityRequest<BusinessProfileResponse>('/api/business-profile'),
  submit: () => identityRequest<SubmitBusinessProfileResponse>('/api/business-profile/submit', { method: 'POST' }),
  clearDraft: () => identityRequest<void>('/api/business-profile/clear-draft', { method: 'POST' }),

  // ---- Step 1: Basic Business Info ----
  saveBasicInfoDraft: (request: SaveBasicInfoRequest) =>
    identityRequest<BasicInfoStepResponse>('/api/business-profile/steps/basic-info/draft', { method: 'PUT', body: JSON.stringify(request) }),
  saveBasicInfoAndContinue: (request: SaveBasicInfoRequest) =>
    identityRequest<BasicInfoStepResponse>('/api/business-profile/steps/basic-info/continue', { method: 'POST', body: JSON.stringify(request) }),

  // ---- Step 2: Business Address ----
  saveAddressDraft: (request: SaveAddressRequest) =>
    identityRequest<AddressStepResponse>('/api/business-profile/steps/address/draft', { method: 'PUT', body: JSON.stringify(request) }),
  saveAddressAndContinue: (request: SaveAddressRequest) =>
    identityRequest<AddressStepResponse>('/api/business-profile/steps/address/continue', { method: 'POST', body: JSON.stringify(request) }),

  // ---- Step 3: Bank Information (PaymentProcessing.Api, authenticated) ----
  getFundingAccount: () => notFoundAsNull(paymentProcessingRequest<FundingAccountResponse>('/api/funding-accounts')),
  saveFundingAccount: (request: CreateFundingAccountRequest) =>
    paymentProcessingRequest<FundingAccountResponse>('/api/funding-accounts', { method: 'PUT', body: JSON.stringify(request) }),
  getFundingAccountDraft: () => notFoundAsNull(paymentProcessingRequest<FundingAccountDraftResponse>('/api/funding-accounts/draft')),
  saveFundingAccountDraft: (request: SaveFundingAccountDraftRequest) =>
    paymentProcessingRequest<FundingAccountDraftResponse>('/api/funding-accounts/draft', { method: 'PUT', body: JSON.stringify(request) }),
  clearFundingAccountDraft: () => paymentProcessingRequest<void>('/api/funding-accounts/draft', { method: 'DELETE' }),

  // ---- Step 4: Payroll Setup (PayrollConfig.Api, authenticated) ----
  getActivePaySchedule: () => notFoundAsNull(payrollConfigRequest<PayScheduleResponse>('/api/pay-schedules/active')),
  savePaySchedule: (request: CreatePayScheduleRequest) =>
    payrollConfigRequest<PayScheduleResponse>('/api/pay-schedules/current', { method: 'PUT', body: JSON.stringify(request) }),
  getPayScheduleDraft: () => notFoundAsNull(payrollConfigRequest<PayScheduleDraftResponse>('/api/pay-schedules/draft')),
  savePayScheduleDraft: (request: SavePayScheduleDraftRequest) =>
    payrollConfigRequest<PayScheduleDraftResponse>('/api/pay-schedules/draft', { method: 'PUT', body: JSON.stringify(request) }),
  clearPayScheduleDraft: () => payrollConfigRequest<void>('/api/pay-schedules/draft', { method: 'DELETE' }),

  // ---- Step 5: Branches / Work Locations (Identity.Api, authenticated) ----
  listWorkLocations: () => identityRequest<WorkLocationResponse[]>('/api/work-locations'),
  createWorkLocation: (request: WorkLocationRequest) =>
    identityRequest<WorkLocationResponse>('/api/work-locations', { method: 'POST', body: JSON.stringify(request) }),
  updateWorkLocation: (id: string, request: WorkLocationRequest) =>
    identityRequest<WorkLocationResponse>(`/api/work-locations/${id}`, { method: 'PUT', body: JSON.stringify(request) }),
  archiveWorkLocation: (id: string) => identityRequest<WorkLocationResponse>(`/api/work-locations/${id}/archive`, { method: 'POST' }),
  reactivateWorkLocation: (id: string) => identityRequest<WorkLocationResponse>(`/api/work-locations/${id}/reactivate`, { method: 'POST' }),
  deleteWorkLocation: (id: string) => identityRequest<void>(`/api/work-locations/${id}`, { method: 'DELETE' }),
  getWorkLocationDraft: () => notFoundAsNull(identityRequest<WorkLocationDraftResponse>('/api/work-locations/draft')),
  saveWorkLocationDraft: (request: SaveWorkLocationDraftRequest) =>
    identityRequest<WorkLocationDraftResponse>('/api/work-locations/draft', { method: 'PUT', body: JSON.stringify(request) }),
  clearWorkLocationDraft: () => identityRequest<void>('/api/work-locations/draft', { method: 'DELETE' }),

  // ---- Step 6: Admin Information (Identity.Api, authenticated) ----
  listMembers: (companyId: string) => identityRequest<MembershipResponse[]>(`/api/companies/${companyId}/memberships`),
  listInvitations: (companyId: string) => identityRequest<MembershipInvitationResponse[]>(`/api/companies/${companyId}/memberships/invitations`),
  createInvitation: (companyId: string, request: CreateMembershipInvitationRequest) =>
    identityRequest<MembershipInvitationResponse>(`/api/companies/${companyId}/memberships/invitations`, {
      method: 'POST',
      body: JSON.stringify(request),
    }),
  revokeInvitation: (companyId: string, invitationId: string) =>
    identityRequest<void>(`/api/companies/${companyId}/memberships/invitations/${invitationId}`, { method: 'DELETE' }),
  setOwnerStatus: (companyId: string, membershipId: string, isOwner: boolean) =>
    identityRequest<MembershipResponse>(`/api/companies/${companyId}/memberships/${membershipId}/owner`, {
      method: 'PUT',
      body: JSON.stringify({ isOwner }),
    }),
};
